import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import { useFeatureFlags } from '@node-space/hooks'
import { Box, Box as Wrapper } from '@node-space/storybook-components/dist/Box'
import { Button } from '@node-space/storybook-components/dist/Button'
import Loader from '@node-space/storybook-components/dist/Loader'
import { PageHeader } from '@node-space/storybook-components/dist/PageHeader'
import { SkeletonCard } from '@node-space/storybook-components/dist/SkeletonLoader'
import { StatusCard } from '@node-space/storybook-components/dist/StatusCard'
import { Text } from '@node-space/storybook-components/dist/Text'
import { Toggle } from '@node-space/storybook-components/dist/Toggle'
import { Approval, PathNames } from 'constants/General'
import { useAccountsContext } from 'hooks/context/useAccountsContext'
import { useAccountApprovalsPreferenceMutation } from 'hooks/mutations/useAccountApprovalsPreferenceMutation'
import { useAccountApprovalsPreferenceQuery } from 'hooks/queries/useAccountApprovalsPreferenceQuery'
import { useApprovalsTransactionsQuery } from 'hooks/queries/useApprovalsTransactionsQuery'
import { useMappedRoles } from 'hooks/useMappedRoles'
import { useToastContext } from 'hooks/useToastContext'
import { ApprovalStatuses } from 'types/approvals'
import ApprovalDecisionModal from './components/ApprovalDecisionModal'
import ApprovalHeaderButtons from './components/ApprovalHeaderButtons'
import ApprovalTabs from './components/ApprovalTabs'
import ManageApproversModal from './components/ManageApproversModal'

const Approvals = () => {
  const { t } = useTranslation()

  const navigate = useNavigate()
  const addToast = useToastContext()
  const { currentAccount } = useAccountsContext()
  const { enableAcl } = useFeatureFlags()
  const permissions = useMappedRoles()?.manageTeamMemberApprovers
  const approvalPermissions = useMappedRoles()?.manageApprovals
  const canEdit = enableAcl ? permissions?.canEdit : true
  const canManageApproval = enableAcl ? approvalPermissions?.canEdit : true

  const currentAccountReference = currentAccount?.reference
  const [accountApprovalsPreference, setAccountApprovalPreference] = useState(false)
  const [showManageApproversModal, setShowManageApproversModal] = useState(false)
  const [approvalDecision, setApprovalDecision] = useState({
    decision: null,
    showApprovalDecisionModal: false,
  })
  const [checkedRowData, setCheckedRowData] = useState<unknown[]>([])
  const [showOtherTabs, setShowOtherTabs] = useState(false)

  const {
    data: approvalPreference,
    isError,
    isSuccess,
    isFetching,
  } = useAccountApprovalsPreferenceQuery(currentAccountReference)
  const { mutate: updateAccountApprovalsPreference, isLoading: togglePreferenceIsProcessing } =
    useAccountApprovalsPreferenceMutation()

  const {
    data: pendingApprovalTransactions,
    isError: isPendingApprovalsError,
    isSuccess: isPendingApprovalSuccess,
    isFetching: isFetchingPendingApprovals,
  } = useApprovalsTransactionsQuery(currentAccountReference, ApprovalStatuses.PENDING)

  useEffect(() => {
    setAccountApprovalPreference(!!approvalPreference?.approvalsRequired)
  }, [approvalPreference])

  const toggleAccountApprovalPreference = () => {
    updateAccountApprovalsPreference(
      {
        preference: Approval.ACCOUNT,
        flow: Approval.FIAT_3RD_PARTY_PAYOUT,
        parentId: currentAccountReference,
        enabled: !accountApprovalsPreference,
      },
      {
        onSuccess: () => {
          setAccountApprovalPreference(!accountApprovalsPreference)
          addToast({
            title: accountApprovalsPreference
              ? t('approvals.disableApprovalToastMessage')
              : t('approvals.enableApprovalToastMessage'),
            state: 'success',
            delay: 5,
          })
        },
        onError: () => {
          addToast({
            title: t('oopsSomethingWentWrongContracted'),
            state: 'error',
            delay: 5,
          })
        },
      }
    )
  }

  const hasPendingTransactions = !!pendingApprovalTransactions?.length

  const handleManageApprovers = () => {
    setShowManageApproversModal(true)
  }

  const handleApprovalDecision = (decision: keyof typeof Approval.DECISION) => {
    setApprovalDecision({ decision, showApprovalDecisionModal: true })
  }

  const handleCloseManageApproversModal = () => {
    setShowManageApproversModal(false)
  }

  const handleCloseApprovalDecisionModal = () => {
    setApprovalDecision({ ...approvalDecision, showApprovalDecisionModal: false })
  }

  return (
    <Wrapper>
      {showManageApproversModal && (
        <ManageApproversModal
          canManageApprovers={canEdit}
          handleClose={handleCloseManageApproversModal}
        />
      )}
      {approvalDecision?.showApprovalDecisionModal && (
        <ApprovalDecisionModal
          decision={approvalDecision?.decision}
          selectedItems={checkedRowData}
          handleCloseApprovalDecisionModal={handleCloseApprovalDecisionModal}
        />
      )}
      {!isError && isFetching && (
        <Box width="full" role="skeletonTab">
          <SkeletonCard name="approvalsSkeletonCard" />
        </Box>
      )}
      {isError && !isFetching && (
        <StatusCard status="error" headline={t('oopsSomethingWentWrong')} />
      )}
      {isSuccess && !isFetching && (
        <>
          <PageHeader
            description={
              <Box flex>
                <Text size="sm" color="grey" className="mr-2">
                  {t('approvals.pageHeaderDescription')}
                </Text>
                <Toggle
                  disabled={!canEdit}
                  value={accountApprovalsPreference}
                  onChange={toggleAccountApprovalPreference}
                />
                {togglePreferenceIsProcessing && <Loader size="small" />}
              </Box>
            }
            renderRightSection={
              hasPendingTransactions && (
                <ApprovalHeaderButtons
                  canManageApprovalTransactions={canManageApproval}
                  handleManageApprovers={handleManageApprovers}
                  handleApprovalDecision={handleApprovalDecision}
                  hasCheckedPendingTransactions={!!checkedRowData?.length}
                />
              )
            }
          />
          {hasPendingTransactions || showOtherTabs ? (
            <ApprovalTabs
              approvalsConfig={{
                isFetchingPendingApprovals,
                accountApprovalsPreference,
                pendingApprovalTransactions,
                isPendingApprovalsError,
                isPendingApprovalSuccess,
                setCheckedRowData,
              }}
              accountReference={currentAccountReference}
              canManageApproval={canManageApproval}
            />
          ) : (
            !isFetchingPendingApprovals && (
              <StatusCard
                headline={
                  accountApprovalsPreference
                    ? t('approvals.enabledApprovalsHeadline')
                    : t('approvals.disabledApprovalsHeadline')
                }
              >
                <Box paddingB={16} className="sm:flex">
                  <Text size="sm">
                    {accountApprovalsPreference
                      ? t('approvals.enabledApprovalsDescription')
                      : t('approvals.disabledApprovalsDescription')}
                    <Link to="/" className="ml-1 text-primary-500" target="_blank">
                      {t('learnMore')}
                    </Link>
                  </Text>
                </Box>
                <Box flex>
                  <Button
                    disabled={!accountApprovalsPreference || !canEdit}
                    onClick={handleManageApprovers}
                  >
                    {t('approvals.manageApprovers')}
                  </Button>
                  <Button
                    secondary
                    className="ml-3"
                    onClick={() => {
                      setShowOtherTabs(true)
                      navigate(`${PathNames.APPROVALS}?tab=${ApprovalStatuses.APPROVED}`, {
                        replace: true,
                      })
                    }}
                  >
                    {t('approvals.viewOtherTransactions', { tabName: `other` })}
                  </Button>
                </Box>
              </StatusCard>
            )
          )}
        </>
      )}
    </Wrapper>
  )
}

export default Approvals
